import ExcelJS from 'exceljs'
import { deliveryTimeByMatrix } from './kpDelivery'
import { DEFAULT_KP_ENTITY, DEFAULT_KP_PHONE, type KpEntity } from './kpEntities'
import type { VatMode } from '@crm/shared'
import { excelPageMarkFooter, KP_EXCEL_TERMS } from './kpTerms'
import { kpTermsFor, kpVatView } from './kpVat'

interface KPItem {
  partNumber: string
  customerPartNumber?: string | null // оригинальный артикул заказчика (колонка «Артикул заказчика»)
  brand: string | null
  quantity: number
  unit: string | null
  priceWithMarkup: number // Руб/шт с НДС — уже с наценкой
  priceNet?: number // Руб/шт без НДС — в КП «НДС сверху»
  matrix?: string // матрица выбранной квоты (stock/china/rf/manuf/other) — для срока поставки
  note?: string | null // примечание к позиции (вводится в предпросмотре КП)
}

interface KPParams {
  requestNumber?: string
  clientName?: string | null
  managerName?: string | null
  managerPhone?: string | null
  managerEmail?: string | null
  /** Юрлицо, от которого выставляется КП (название/реквизиты/логотип в шапке). */
  entity?: KpEntity
  /** Режим НДС КП: «С НДС» (по умолчанию) или «НДС сверху» — цены, итоги, первый пункт условий. */
  vatMode?: VatMode
  items: KPItem[]
}

async function loadLogoPng(url: string): Promise<string | null> {
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const buf = await res.arrayBuffer()
    const bytes = new Uint8Array(buf)
    let bin = ''
    bytes.forEach((b) => (bin += String.fromCharCode(b)))
    return btoa(bin)
  } catch {
    return null
  }
}

export async function generateKP({
  requestNumber,
  clientName,
  managerName,
  managerPhone,
  managerEmail,
  entity = DEFAULT_KP_ENTITY,
  vatMode,
  items,
}: KPParams) {
  const view = kpVatView(items, vatMode)
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('КП', {
    // По ширине — один лист, по высоте — сколько нужно: длинное КП печатается
    // на нескольких листах, а не сжимается в один мелким шрифтом.
    pageSetup: {
      paperSize: 9,
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.5, right: 0.5, top: 0.6, bottom: 0.75, header: 0.3, footer: 0.3 },
    },
  })

  ws.columns = [
    { width: 5 }, // A — №
    { width: 22 }, // B — Артикул заказчика
    { width: 22 }, // C — Артикул
    { width: 18 }, // D — Производитель
    { width: 6 }, // E — Ед.шт.
    { width: 8 }, // F — кол-во
    { width: 12 }, // G — Руб/шт c НДС
    { width: 14 }, // H — Всего Руб c НДС
    { width: 12 }, // I — Срок поставки
    { width: 16 }, // J — Примечание
  ]

  const font = (size: number, bold = false): Partial<ExcelJS.Font> => ({
    name: 'Times New Roman',
    size,
    bold,
  })
  const border: Partial<ExcelJS.Borders> = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  }
  const center: Partial<ExcelJS.Alignment> = {
    horizontal: 'center',
    vertical: 'middle',
    wrapText: true,
  }

  let row = 1

  // ── Шапка компании — имя (A1:J1) ──
  ws.mergeCells(`A${row}:J${row}`)
  const companyRow1 = ws.getRow(row)
  companyRow1.getCell(1).value = entity.excelTitle
  companyRow1.getCell(1).font = font(14, true)
  companyRow1.getCell(1).alignment = { ...center, wrapText: true }
  companyRow1.height = 55
  row++

  // Реквизиты (B2:J2) — A2 оставляем под лого
  ws.mergeCells(`B${row}:J${row}`)
  const reqRow = ws.getRow(row)
  reqRow.getCell(2).value = entity.excelRequisites
  reqRow.getCell(2).font = font(10)
  reqRow.getCell(2).alignment = { ...center, wrapText: true }
  reqRow.height = 80

  // ── Лого (абсолютное позиционирование в A2) ──
  const logoBase64 = await loadLogoPng(entity.logo.url)
  if (logoBase64) {
    const imageId = wb.addImage({ base64: logoBase64, extension: 'png' })
    ws.addImage(imageId, {
      tl: { col: 0, row: 1 },
      ext: { width: entity.logo.width, height: entity.logo.height },
    })
  }
  row++

  // ── КП № ... | Клиент ──
  const today = new Date()
  const dateStr = today.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const kpNum = requestNumber ?? '—'
  // Внизу каждого листа печати: «КП № … от … · юрлицо · клиент · Лист N из M».
  ws.headerFooter.oddFooter = excelPageMarkFooter({
    requestNumber: kpNum,
    dateStr,
    entityName: entity.shortName,
    clientName,
  })
  const clientLabel = clientName?.trim() || ''

  // Левая часть: КП №
  ws.mergeCells(`A${row}:E${row}`)
  const kpLeftCell = ws.getRow(row).getCell(1)
  kpLeftCell.value = `КП № ${kpNum} от ${dateStr} г.`
  kpLeftCell.font = font(12, true)
  kpLeftCell.alignment = { horizontal: 'left', vertical: 'middle' }

  // Правая часть: имя клиента
  ws.mergeCells(`F${row}:J${row}`)
  const kpRightCell = ws.getRow(row).getCell(6)
  kpRightCell.value = clientLabel
  kpRightCell.font = font(12, true)
  kpRightCell.alignment = { horizontal: 'right', vertical: 'middle' }
  ws.getRow(row).height = 25
  row++

  // ── Заголовок таблицы ──
  const headers = [
    '№\nп/п',
    'Артикул\nзаказчика',
    'Артикул',
    'Производитель',
    'Ед.\nшт.',
    'кол-во',
    view.onTop ? 'Руб/шт\nбез НДС' : 'Руб/шт\nc НДС',
    view.onTop ? 'Всего Руб\nбез НДС' : 'Всего Руб\nc НДС',
    'Срок\nпоставки',
    'Примечание',
  ]
  const headerRow = ws.getRow(row)
  headers.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1)
    cell.value = h
    cell.font = font(9, true)
    cell.alignment = center
    cell.border = border
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } }
  })
  headerRow.height = 30
  // Шапка таблицы повторяется на каждом листе печати.
  ws.pageSetup.printTitlesRow = `${row}:${row}`
  row++

  // ── Строки с данными (только реальные позиции, без пустых) ──
  // Цена и сумма строки — из вида цен КП (kpVatView): «С НДС» или «НДС сверху».
  items.forEach((item, idx) => {
    const { price, sum } = view.lines[idx]!

    const r = ws.getRow(row)
    const vals = [
      idx + 1,
      item.customerPartNumber ?? item.partNumber,
      item.partNumber,
      item.brand ?? '',
      item.unit ?? 'шт',
      item.quantity,
      price,
      sum,
      deliveryTimeByMatrix(item.matrix),
      item.note?.trim() ?? '',
    ]
    // Текстовые колонки (Артикул заказчика, Артикул, Производитель, Примечание)
    // переносятся внутри ячейки — длинный текст не вылезает и не обрезается.
    // Остальные — по центру. Высоту строки Excel подберёт автоматически.
    const wrapCols = new Set([1, 2, 3, 9])
    vals.forEach((v, i) => {
      const cell = r.getCell(i + 1)
      cell.value = v
      cell.font = font(10)
      cell.border = border
      cell.alignment = wrapCols.has(i) ? { vertical: 'middle', wrapText: true } : { ...center }
    })
    r.getCell(7).numFmt = '#,##0.00' // Руб/шт
    r.getCell(8).numFmt = '#,##0.00' // Всего Руб
    row++
  })

  // ── Итоги: «ВСЕГО с НДС 22%» и «В том числе НДС» или «Итого без НДС», «НДС», «Всего к оплате» ──
  for (const t of view.rows) {
    ws.mergeCells(`A${row}:F${row}`)
    const tr = ws.getRow(row)
    tr.getCell(1).value = t.label
    tr.getCell(1).font = font(10, true)
    tr.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' }
    tr.getCell(1).border = border
    ws.mergeCells(`G${row}:H${row}`)
    tr.getCell(7).value = t.value
    tr.getCell(7).numFmt = '#,##0.00 "₽"'
    tr.getCell(7).font = font(10, true)
    tr.getCell(7).alignment = center
    tr.getCell(7).border = border
    row++
  }
  row++

  // ── Примечания (по шаблону) ──
  const notes = kpTermsFor(KP_EXCEL_TERMS, view.onTop)

  notes.forEach((note) => {
    ws.mergeCells(`B${row}:J${row}`)
    const r = ws.getRow(row)
    r.getCell(2).value = note
    r.getCell(2).font = font(9)
    r.getCell(2).alignment = { wrapText: true, vertical: 'top' }
    if (note.length > 100) r.height = 50
    if (note.length > 300) r.height = 80
    row++
  })

  // ── Исполнитель / Ответственный ──
  row += 2
  const responsible = managerName?.trim() || ''
  ws.mergeCells(`B${row}:J${row}`)
  ws.getRow(row).getCell(2).value = `Исполнитель: ${responsible}`
  ws.getRow(row).getCell(2).font = font(10)
  row++
  ws.mergeCells(`B${row}:J${row}`)
  ws.getRow(row).getCell(2).value = `Ответственный: ${responsible}`
  ws.getRow(row).getCell(2).font = font(10)
  row++
  ws.mergeCells(`B${row}:J${row}`)
  ws.getRow(row).getCell(2).value = `Телефон: ${managerPhone?.trim() || DEFAULT_KP_PHONE}`
  ws.getRow(row).getCell(2).font = font(10)
  // Почта — строкой под телефоном, только если заполнена в профиле менеджера.
  const email = managerEmail?.trim()
  if (email) {
    row++
    ws.mergeCells(`B${row}:J${row}`)
    ws.getRow(row).getCell(2).value = `Почта: ${email}`
    ws.getRow(row).getCell(2).font = font(10)
  }

  // ── Скачивание ──
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  // Имя файла: «<клиент> КП <номер> от <дата>». Из имени клиента убираем
  // символы, недопустимые в именах файлов (/ \ : * ? " < > |).
  const clientPrefix = clientName ? `${clientName.replace(/[/\\:*?"<>|]/g, '').trim()} ` : ''
  a.download = `${clientPrefix}КП ${kpNum} от ${dateStr}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}
